import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { defaultSYS3010_R01Filter, SYS3010_R01Filter, useSYS3010_R01Store } from "src/containers/mes/SYS/SYS3010_R01/Store_SYS3010_R01";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { deleteSYS3010_R01, downloadExcel_SYS3010_R01, getSYS3010_R01AllCount, getSYS3010_R01List, patchSYS3010_R01, postSYS3010_R01 } from "./Api_SYS3010_R01";
import { defaultSYS3010_R01Dto, SYS3010_R01Dto } from "./Dto_SYS3010_R01";
import { AgGridReact } from "ag-grid-react";
import {  fetchFilterConfig, InsertRowHandler } from "src/utils/grid/GridHandlerContent";
import { buildCommonCodeRequirements, SYS3010_R01_ROW_VALIDATE } from "src/utils/commonCode/commonCodeRequirementConfig";
import { useCommonCodeValidate } from "src/utils/commonCode/CommonCodeValidateContext";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";


interface UserContentProps {
	gridRef: React.RefObject<AgGridReact<SYS3010_R01Dto> | null>;
	setMessage: (message: string) => void;  
	
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}


export const Content_SYS3010_R01 = (props: UserContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const { setLoadingSpinner } = useLoadingSpinnerStore();

	const {
		SYS3010_R01List, setSYS3010_R01List, insertSYS3010_R01List,
		deletedSYS3010_R01List, addDeletedSYS3010_R01List, removeDeletedSYS3010_R01List,
		selectedSYS3010_R01List, setSelectedSYS3010_R01List, removeSelectedSYS3010_R01List,
		setOriginalSYS3010_R01List,
		setAllCount,
		initData,
		setScrollTop,
	} = useSYS3010_R01Store();

	const { 
		SYS3010_R01Filter, setSYS3010_R01Filter,
		SYS3010_R01PastFilter, setSYS3010_R01PastFilter
	} = useSYS3010_R01Store();

	// 원본 데이터 저장을 위한 상태 추가
	const [originalUserList, setOriginalUserList] = useState<SYS3010_R01Dto[]>([]);
	// 권한 정보 로드
	useFetchActionbarAuth("SYS3010_R01");

	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { yesNoList } = useCommonCodeStore();

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});

    const [clickable, setClickable] = useState(true);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const { isDefaultFilter,  listLength } = fetchFilterConfig(
			SYS3010_R01Filter,
			SYS3010_R01PastFilter,
			defaultSYS3010_R01Filter,
			SYS3010_R01List.length
		);

		const response = await downloadExcel_SYS3010_R01(
			isDefaultFilter? '' : 'search',
			getParams(SYS3010_R01Filter),
			listLength
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '로그인정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [SYS3010_R01Filter, SYS3010_R01List, setLoadingSpinner]);

	/**
	 * 사용자명 입력 변경 핸들러
	 * 검색어 입력 시 호출
	 */
	const handleUserNmChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSYS3010_R01Filter({...SYS3010_R01Filter, userNm: e.target.value});
	}

	const defaultSYS3010_R01Row = useCallback(() => {
		return {
			...defaultSYS3010_R01Dto,
			inserted: true,
		}
	}, [defaultSYS3010_R01Dto]);

	const getParams = (filter: SYS3010_R01Filter) => {

		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(SYS3010_R01_ROW_VALIDATE, {
				yesNoList,
			}),
		);
		if (!commonResult.valid) { return { ...defaultSYS3010_R01Dto }; }

		return {
			...defaultSYS3010_R01Dto,
			userNm: filter.userNm,
			costAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			approveAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			deleteAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			lockSts: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			inserted: true,
		}
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getSYS3010_R01AllCount(getParams(SYS3010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SYS3010_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		if (fetchType === 'search') {
			setScrollTop(null);
		}
	
		let initialListLength = SYS3010_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setSYS3010_R01List([]);
		}
	
		const responseData = await getSYS3010_R01List(
			fetchType === 'refetch' ? 'refetch' : '',
			getParams(SYS3010_R01Filter),
			fetchType === 'refetch' ? initialListLength : SYS3010_R01List.length ?? 0
		);
	
		// 조회 시 비밀번호 필드를 빈 값으로 처리 (표준 패턴: 보안상 비밀번호는 조회 시 노출하지 않음)
		let newList = [...responseData.data.data ].map(user => ({
			...user,
			password: '' // 조회 시 비밀번호는 항상 빈 값으로 처리
		})) as SYS3010_R01Dto[];
		
		setSYS3010_R01List(newList);
		
		const processAllCount = await renewalAllCount();
		// 원본 데이터도 비밀번호를 빈 값으로 저장 (비교용)
		setOriginalSYS3010_R01List(newList.map(user => ({ ...user, password: '' })));
		setSelectedSYS3010_R01List([]);
		removeDeletedSYS3010_R01List(deletedSYS3010_R01List);
		gridRef.current?.api.ensureIndexVisible(0, 'top');
	
		if (newList.length === 0) {
			// insertSYS3010_R01List([{ ...defaultSYS3010_R01Row, inserted: true }], 0);
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		} else {
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			}
		}
	
		setSYS3010_R01PastFilter(SYS3010_R01Filter);
		setLoadingSpinner(false);
	}, [SYS3010_R01Filter, SYS3010_R01PastFilter, SYS3010_R01List, deletedSYS3010_R01List, gridRef, setMessage, setLoadingSpinner, setOriginalSYS3010_R01List, setSelectedSYS3010_R01List, insertSYS3010_R01List, renewalAllCount, setScrollTop]);
	
	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		executeDataFetch('search');
	}, [executeDataFetch]);
	


	const validateInsertData = (data: SYS3010_R01Dto, currentRowIndex: number, isNew: boolean = false) => {
        if (!validateString(data.userId)) {
            showWarning("사용자ID를 입력해주세요.");
            return false;
        }
        if (!validateString(data.userNm)) {
            showWarning("사용자명을 입력해주세요.");
            return false;
        }
		if (!validateString(data.empNo)) {
            showWarning("사원번호를 입력해주세요.");
            return false;
        }
		// 신규 사용자는 비밀번호 필수, 수정 사용자는 비밀번호가 입력된 경우에만 검증
		if (isNew || (data.password && data.password.trim().length > 0)) {
			if (!validateString(data.password)) {
				showWarning("비밀번호를 입력해주세요.");
				return false;
			}
		}

		// 1. 삭제하기로 표시된 사용자들을 먼저 제외
		const activeUsers = SYS3010_R01List.filter(user => !deletedSYS3010_R01List.includes(user.userId));
		// 현재 검증 중인 행의 인덱스를 제외한 나머지 행들
		const otherUsers = activeUsers.filter((_, index) => index !== currentRowIndex);
		
		if (otherUsers.some(user => user.userId === data.userId)) {
			showWarning("중복된 사용자ID가 있습니다.\n아이디: " + data.userId);
			return false;
		}
		return true;
	}

	const initContainerData = useCallback(async () => {
		initData();
	}, [initData, setMessage]);

	const handleInsertClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		InsertRowHandler({
			event: e,
			dataList: SYS3010_R01List,
			rowData: {...defaultSYS3010_R01Row(), valueIndex: -1},
			insertRow: insertSYS3010_R01List,
			defaultRowData: defaultSYS3010_R01Row(),
			setClickable
		});
	}, [SYS3010_R01List, insertSYS3010_R01List, setClickable]);
	
	
	const handleSaveClick = useCallback(async () => {

		// 신규 사용자 처리 (비밀번호는 필수)
		const insertedList: SYS3010_R01Dto[] = SYS3010_R01List.filter(row => 
			!deletedSYS3010_R01List.includes(row.userId) && 
			row.inserted === true
		).map(row => {
			const { password, ...rest } = row;
			return {
				...rest,
				password: password || '', // 신규는 비밀번호 필수
				passUpdate: formatDate(new Date()),
				remark: ''
			};
		});

		// 수정 사용자 처리 (비밀번호가 빈 값이면 필드 제외 - 기존 비밀번호 유지)
		const modifiedList : SYS3010_R01Dto[] = SYS3010_R01List.filter(row => 
			!deletedSYS3010_R01List.includes(row.userId) && 
			row.isModified && !row.inserted
		).map(row => {
			const { password, ...rest } = row;
			// 비밀번호가 빈 값이면 password 필드를 제외 (백엔드가 기존 값 유지)
			const result: any = {
				...rest,
				passUpdate: formatDate(new Date()),
				remark: ''
			};
			// 비밀번호가 입력된 경우에만 포함
			if (password && password.trim().length > 0) {
				result.password = password;
			}
			return result;
		});
		
        // 수정 사용자 유효성 검증 (비밀번호는 선택사항)
		let seenMenuSet = new Set<string>();
		let valid = true;
		modifiedList.forEach((value) => {
			let index = SYS3010_R01List.findIndex(user => user.userId === value.userId);
			if(!validateInsertData(value, index, false)) valid = false; // false = 수정 사용자
			seenMenuSet.add(value.userId);
		});
		if(!valid) { return; }
        // 신규 사용자 유효성 검증 (비밀번호 필수)
		seenMenuSet = new Set<string>();
		valid = true;
		insertedList.forEach((value) => {
			let index = SYS3010_R01List.findIndex(user => user.userId === value.userId);
			if(!validateInsertData(value, index, true)) valid = false; // true = 신규 사용자
			seenMenuSet.add(value.userId);
		});
		if(!valid) { return; }

		const deleteStringList = deletedSYS3010_R01List.map(item => item.toString());
		const deleteTargetList = SYS3010_R01List.filter(item => deleteStringList.includes(item.valueIndex.toString()));

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			insertedList.length > 0 ? postSYS3010_R01(insertedList) : Promise.resolve(),
			modifiedList.length > 0 ? patchSYS3010_R01(modifiedList) : Promise.resolve(),
			deletedSYS3010_R01List.length > 0 ? Promise.all(deleteTargetList.map(value => deleteSYS3010_R01(value.userId))) : Promise.resolve()
		]);


		if(insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('로그인정보 저장이 완료되었습니다.');
			setMessage('로그인정보 저장이 완료되었습니다.');
			setTimeout(() => { setMessage(''); }, 5000);
		} else if(deleteResponse) {
			showSuccess('로그인정보 삭제가 완료되었습니다.');
			setMessage('로그인정보 삭제가 완료되었습니다.');
		}
		setTimeout(() => { setMessage(''); }, 5000);

		await executeDataFetch('refetch', { messageMode: 'none' });
	}, [SYS3010_R01List, deletedSYS3010_R01List, originalUserList]);
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedSYS3010_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedSYS3010_R01List(deletedSYS3010_R01List);
			removeSelectedSYS3010_R01List();
			setSYS3010_R01List([...SYS3010_R01List]);
		} else if (selectedSYS3010_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedSYS3010_R01List(selectedSYS3010_R01List);
			removeSelectedSYS3010_R01List(selectedSYS3010_R01List);
		}
	}, [selectedSYS3010_R01List, deletedSYS3010_R01List, addDeletedSYS3010_R01List, removeDeletedSYS3010_R01List, removeSelectedSYS3010_R01List, setSYS3010_R01List, SYS3010_R01List]);

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedSYS3010_R01List.length === 0 && selectedSYS3010_R01List.length === 0,
			save: SYS3010_R01List.length === 0,
			print: SYS3010_R01List.length === 0,
			excel: SYS3010_R01List.length === 0
		}));
	}, [selectedSYS3010_R01List, deletedSYS3010_R01List, SYS3010_R01List]);
	
	
	// userList가 변경될 때마다 원본 데이터 복사
	useEffect(() => {
		setOriginalUserList(JSON.parse(JSON.stringify(SYS3010_R01List)));
	}, [SYS3010_R01List]);

	return{
		clickable, setClickable,
		buttonDisabled,
		handleExcelClick,
		handleUserNmChange,
		defaultSYS3010_R01Row,
		fetchData,
		initContainerData,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
	}
}
